import { useEffect, useState, useCallback } from "react";

import { getDoc, serverTimestamp, increment } from "firebase/firestore";
import { getBatch, getRef } from "lib/firebase";
import { useSetRecoilState } from "recoil";
import { userState } from "pages/_app";
import { createNotif } from "lib/createNotif";
import { sendNotifMail } from "lib/sendEmail";

import { CurrentUser } from "lib/type";

let isChangingLike = false;

export const useLike = (
  author: CurrentUser,
  user: CurrentUser,
  articleID = "",
  articleTitle: string,
  color: string,
  thumb: string,
  createdAt: string
) => {
  const [isPast, setIsPast] = useState<boolean>();
  useEffect(() => {
    if (!author || !user || !articleID) return;
    const checkLikedPast = async () => {
      const likedUserPath = `${author.uid}/publicArticles/${articleID}/likedUsers/${user.uid}`;
      const likedRef = getRef("users", likedUserPath);
      const likedDoc = await getDoc(likedRef).catch((err) => {
        console.log(err);
        alert("過去にいいねしたことあるか識別する情報の取得に失敗しました。");
        return null;
      });
      if (!likedDoc) return;
      setIsPast(likedDoc.exists() ? true : false);
    };
    checkLikedPast();
  }, [articleID, author, user]);

  const [isCurrentLiked, setIsCurrentLiked] = useState(false);
  useEffect(() => {
    if (!author || !user || !articleID) return;
    const loadCurrentLiked = async () => {
      const currentLikedRef = getRef(
        "users",
        `${user.uid}/likedArticles/${articleID}`
      );
      const currentLikedDoc = await getDoc(currentLikedRef);
      setIsCurrentLiked(currentLikedDoc.exists() ? true : false);
    };
    loadCurrentLiked();
  }, [articleID, author, user]);

  const [currentLikeCount, setCurrentLikeCount] = useState(0);
  useEffect(() => {
    if (!author || !articleID) return;
    const loadCurrentLikeCount = async () => {
      const articleRef = getRef(
        "users",
        `${author.uid}/publicArticles/${articleID}`
      );
      const articleDoc = await getDoc(articleRef).catch((err) => {
        console.log(err);
        alert("いいね数の取得に失敗しました。");
        return null;
      });
      if (!articleDoc || !articleDoc.exists()) return;
      const articleData = articleDoc.data();
      const count = articleData.likeCount;
      setCurrentLikeCount(count < 0 ? 0 : count);
    };
    loadCurrentLikeCount();
  }, [articleID, author]);

  const setUser = useSetRecoilState(userState);
  const changeLike = useCallback(async () => {
    if (!author || !user || isChangingLike) return;
    isChangingLike = true;
    const batch = getBatch();
    const authorRef = getRef("users", author.uid);
    batch.update(authorRef, {
      totalLikeCount: isCurrentLiked ? increment(-1) : increment(1),
    });
    const articlePath = `${author.uid}/publicArticles/${articleID}`;
    const articleRef = getRef("users", articlePath);
    batch.update(articleRef, {
      likeCount: isCurrentLiked ? increment(-1) : increment(1),
    });
    const myLikedRef = getRef(
      "users",
      `${user.uid}/likedArticles/${articleID}`
    );
    isCurrentLiked
      ? batch.delete(myLikedRef)
      : batch.set(myLikedRef, {
          articleID: articleID,
          articleTitle: articleTitle,
          thumb: thumb,
          articleCreatedAt: createdAt,
          color: color,
          createdAt: serverTimestamp(),
        });
    if (isPast === false && user.uid !== author.uid) {
      const likedUserRef = getRef(
        "users",
        `${articlePath}/likedUsers/${user.uid}`
      );
      batch.set(likedUserRef, {
        likedUid: user.uid,
        articleID: articleID,
        createdAt: serverTimestamp(),
      });
      await createNotif(
        "like",
        batch,
        { id: articleID, title: articleTitle },
        user,
        author
      );
    }
    batch
      .commit()
      .then(async () => {
        setCurrentLikeCount((prevLikeCount) =>
          isCurrentLiked ? prevLikeCount - 1 : prevLikeCount + 1
        );
        setIsCurrentLiked(isCurrentLiked ? false : true);
        setUser({
          ...user,
          totalLikeCount: isCurrentLiked
            ? user.totalLikeCount - 1
            : user.totalLikeCount + 1,
        });
        if (author.uid !== user.uid && isPast === false && author.isSendNotif)
          sendNotifMail(author, user, "like", articleID, articleTitle);
      })
      .catch((err) => {
        console.log(err);
        alert("いいねに失敗しました。");
      });
    isChangingLike = false;
  }, [
    articleID,
    articleTitle,
    author,
    color,
    createdAt,
    isCurrentLiked,
    isPast,
    setUser,
    thumb,
    user,
  ]);

  return { isCurrentLiked, changeLike, currentLikeCount };
};
